import { useState } from 'react';
import { motion } from 'motion/react';
import { Bot, Check, Crown, LayoutGrid, Medal, Swords, Trophy, X } from 'lucide-react';
import type { RankingLine } from '@gmp/shared-types/browser';
import { Avatar, Confetti, Countdown, Stage, usePartyUi } from '@gmp/shared-ui';
import { party } from '../net/client';
import { Mascot } from '../app/Mascot';
import { gameInfo } from '../app/catalog';

const PODIUM_HEIGHT = ['h-40', 'h-28', 'h-20'];

function Podium({ ranking, big }: { ranking: RankingLine[]; big?: boolean }) {
  const top = ranking.slice(0, 3);
  const order = [top[1], top[0], top[2]].filter(Boolean) as RankingLine[];
  return (
    <div className="flex items-end justify-center gap-3 w-full" aria-label="Podium">
      {order.map((r) => {
        const place = ranking.indexOf(r);
        // Colors and heights follow the rank, so tied winners both stand on gold.
        const tier = Math.min(2, r.rank - 1);
        return (
          <motion.div
            key={r.playerId}
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: place === 0 ? 1.1 : place === 1 ? 0.6 : 0.2, type: 'spring', stiffness: 140, damping: 14 }}
            className="flex flex-col items-center gap-2 flex-1 max-w-[180px]"
          >
            {r.rank === 1 && <Crown className="text-brand-2" size={big ? 48 : 32} />}
            <Avatar id={r.avatar} size={big ? 120 : tier === 0 ? 84 : 64} color={r.color} mood={r.rank === 1 ? 'grin' : 'smile'} />
            <p className={`font-display font-black text-center leading-tight ${big ? 'text-3xl' : ''}`}>{r.nickname}</p>
            {r.title && <p className="text-xs text-mute text-center">{r.title}</p>}
            <div className={`w-full rounded-t-2xl ${PODIUM_HEIGHT[tier]} grid place-items-start justify-center pt-2`} style={{ background: tier === 0 ? 'var(--accent-2)' : tier === 1 ? '#c0c7d6' : '#d08a54' }}>
              <span className="font-display font-black text-ink text-3xl">{r.rank}</span>
            </div>
            <p className="font-display font-black tabular-nums">{r.score.toLocaleString()}</p>
          </motion.div>
        );
      })}
    </div>
  );
}

export function ResultsBody({ big }: { big?: boolean }) {
  const { room, me, reducedMotion } = usePartyUi();
  const res = room.lastResults;
  const won = !!res?.ranking.find((r) => r.playerId === me && r.rank === 1);
  // The fanfare (or your own result's jingle) is played by the platform when the results arrive.
  if (!res) return null;
  const g = gameInfo(res.gameId);
  return (
    <div className="flex flex-col items-center gap-6 w-full" data-testid="final-results">
      {!reducedMotion && <Confetti count={won || big ? 70 : 30} />}
      <div className="text-center flex flex-col items-center">
        <Mascot pose="trophy" size={big ? 200 : 130} />
        <span className="chip uppercase tracking-widest" style={{ color: 'var(--accent)' }}><Trophy size={14} /> {g.name} · final results</span>
        <h1 className={`font-display font-black mt-2 ${big ? 'text-7xl' : 'text-4xl'}`}>
          {res.ranking.filter((r) => r.rank === 1).map((r) => r.nickname).join(' & ')} {res.ranking.filter((r) => r.rank === 1).length > 1 ? 'tie for the win!' : 'wins!'}
        </h1>
        {(res as { endedEarly?: boolean }).endedEarly && <p className="text-mute">The host ended this game early, so it doesn’t count toward the championship.</p>}
      </div>
      <Podium ranking={res.ranking} big={big} />
      <ol className="w-full grid gap-2">
        {res.ranking.slice(3).map((r) => (
          <li key={r.playerId} className={`flex items-center gap-3 rounded-2xl px-3 py-2 ${r.playerId === me ? 'bg-ink-3 ring-2 ring-[var(--accent)]' : 'bg-ink-2'}`}>
            <span className="font-display font-black w-8 text-center text-mute">{r.rank}</span>
            <Avatar id={r.avatar} size={32} color={r.color} />
            <span className="font-bold flex-1 truncate">{r.nickname} <span className="text-mute text-xs">{r.title}</span></span>
            <span className="font-display font-black">{r.score.toLocaleString()}</span>
          </li>
        ))}
      </ol>
      {res.highlights.length > 0 && (
        <ul className="w-full grid gap-1 text-sm text-mute">
          {res.highlights.map((h) => <li key={h} className="flex gap-2"><Medal size={16} className="shrink-0" /> {h}</li>)}
        </ul>
      )}
      {room.settings.championship && room.championship.length > 0 && <Championship big={big} />}
    </div>
  );
}

export function Championship({ big }: { big?: boolean }) {
  const { room, me } = usePartyUi();
  return (
    <section className="card p-4 w-full" aria-label="Party Championship">
      <h2 className={`font-display font-black mb-1 ${big ? 'text-3xl' : 'text-xl'}`}>Party Championship</h2>
      <p className="text-mute text-xs mb-3">Placement points from every finished game (10, 8, 6, 5, 4, 3, 2, then 1), so no game counts more than another.</p>
      <ol className="grid gap-1.5">
        {room.championship.map((c) => (
          <li key={c.playerId} className={`flex items-center gap-3 rounded-xl px-2 py-1.5 ${c.playerId === me ? 'bg-ink-3' : ''}`}>
            <span className="font-display font-black w-6 text-center">{c.rank}</span>
            <Avatar id={c.avatar} size={28} color={c.color} />
            <span className="flex-1 font-bold truncate">{c.nickname}</span>
            <span className="text-mute text-xs">{c.gamesPlayed} game{c.gamesPlayed === 1 ? '' : 's'}</span>
            <span className="font-display font-black w-12 text-right">{c.points}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

/**
 * Grudge Match: any player from the finished game opts in to a rematch of the same game with the same
 * players. The server runs the window (countdown, who's in) and starts it; this only shows it and sends intents.
 * On the TV (`readOnly`) it shows the window without a button.
 */
export function GrudgeMatch({ readOnly, big }: { readOnly?: boolean; big?: boolean }) {
  const { room, me, member, sound } = usePartyUi();
  const [busy, setBusy] = useState(false);
  const w = room.rematch ?? null;
  const res = room.lastResults;
  const self = member(me);
  // Seats come from the finished game; the server decides, this only hides a button that would be refused.
  const eligible = w ? w.eligible.includes(me ?? '') : !!res && !!self && !self.spectator && !self.bot && res.ranking.some((r) => r.playerId === me);
  const mine = !!w && w.optedIn.includes(me ?? '');
  const send = async (optIn: boolean) => {
    setBusy(true);
    sound.play(optIn ? 'rematch' : 'unready', { scope: 'personal' });
    const r = await party.rematch(optIn);
    setBusy(false);
    if (!r.ok) {
      sound.play('error');
      party.notify(r.error ?? 'Could not join the Grudge Match', 'bad');
    }
  };
  if (!w) {
    if (readOnly) return room.rematchNotice ? <p className="text-center text-mute" data-testid="grudge-notice">{room.rematchNotice}</p> : null;
    return (
      <div className="grid gap-2">
        {room.rematchNotice && <p className="text-center text-mute text-sm" role="status" data-testid="grudge-notice">{room.rematchNotice}</p>}
        {eligible && (
          <button className="btn text-2xl min-h-20 font-black uppercase tracking-wide" disabled={busy} onClick={() => void send(true)} data-testid="grudge-match"
            style={{ background: 'var(--color-bad)', color: 'white' }}>
            <Swords size={28} /> Grudge Match!
          </button>
        )}
      </div>
    );
  }
  const seats = [...w.eligible, ...w.bots];
  const inCount = w.optedIn.length + w.bots.length;
  const waitingOn = w.eligible.filter((id) => !w.optedIn.includes(id) && member(id)?.connected);
  return (
    <section className={`card p-4 grid gap-3 ${big ? 'text-xl' : ''}`} aria-label="Grudge Match" data-testid="grudge-window">
      <div className="flex items-center gap-3">
        <Swords className="shrink-0" size={big ? 40 : 28} style={{ color: 'var(--color-bad)' }} />
        <div className="flex-1 min-w-0">
          <h2 className={`font-display font-black leading-tight ${big ? 'text-4xl' : 'text-2xl'}`}>Grudge Match!</h2>
          <p className="text-mute text-sm" data-testid="grudge-count">
            {inCount}/{seats.length} in · {waitingOn.length ? 'starts when everyone’s in or time runs out' : 'starting…'}
            {inCount < w.minPlayers ? ` · needs ${w.minPlayers}` : ''}
          </p>
        </div>
        <Countdown endsAt={w.endsAt} startedAt={w.startedAt} size={big ? 80 : 56} label="Grudge Match starts in" quiet />
      </div>
      <ul className="flex flex-wrap gap-3 justify-center" aria-label="Who's in">
        {seats.map((id) => {
          const m = member(id);
          const isIn = w.optedIn.includes(id) || w.bots.includes(id);
          return (
            <li key={id} className={`flex flex-col items-center gap-1 w-16 ${isIn ? '' : 'opacity-40'}`} data-testid={`grudge-seat-${id}`} data-in={isIn}
              aria-label={`${m?.nickname ?? 'Player'}${isIn ? ', in' : ', not in yet'}`}>
              <div className="relative">
                <Avatar id={m?.avatar ?? 'blob'} size={big ? 64 : 44} color={m?.color} mood={isIn ? 'grin' : 'smile'} />
                <span className={`absolute -bottom-1 -right-1 size-5 rounded-full grid place-items-center ${isIn ? 'bg-good text-ink' : 'bg-ink-3 text-mute'}`}>
                  {w.bots.includes(id) ? <Bot size={12} /> : isIn ? <Check size={12} /> : <X size={12} />}
                </span>
              </div>
              <span className="text-xs font-bold truncate w-full text-center">{m?.nickname ?? 'Player'}</span>
            </li>
          );
        })}
      </ul>
      {!readOnly && (eligible ? (
        mine ? (
          <button className="btn btn-secondary" disabled={busy} onClick={() => void send(false)} data-testid="grudge-out" aria-pressed="true">
            <X size={18} /> I’m out
          </button>
        ) : (
          <button className="btn text-xl min-h-16 font-black" disabled={busy} onClick={() => void send(true)} data-testid="grudge-match"
            style={{ background: 'var(--color-bad)', color: 'white' }}>
            <Swords /> I’m in!
          </button>
        )
      ) : (
        <p className="text-center text-mute text-sm">The players from the last game are lining up a rematch. You’ll watch this one.</p>
      ))}
    </section>
  );
}

export function FinalResults() {
  const { room } = usePartyUi();
  const isHost = room.you.isHost;
  return (
    <Stage game={room.lastResults?.gameId}>
      <main className="flex-1 flex flex-col gap-6 safe-pad safe-top safe-bottom py-6 max-w-2xl w-full mx-auto">
        <ResultsBody />
        <div className="grid gap-3 sticky bottom-0 -mx-4 px-4 pt-6 pb-4 bg-gradient-to-t from-[var(--stage)] via-[var(--stage)] to-transparent">
          <GrudgeMatch />
          {isHost ? (
            // Grudge Match is the rematch; the host's other choice is a different game.
            <button className="btn btn-secondary" onClick={() => party.host({ cmd: 'back_to_lobby' })} data-testid="back-to-lobby">
              <LayoutGrid size={18} /> Pick another game
            </button>
          ) : (
            <p className="text-center text-mute">The host picks what’s next.</p>
          )}
        </div>
      </main>
    </Stage>
  );
}
